import { PurchaseOrderCreatedEvent } from '@rematter/frontend-lib-api-client/grpc/trade/purchase_order_events_pb';
import { PurchaseOrderStatus } from '@rematter/frontend-lib-api-client/grpc/trade/purchase_order_pb';
import {
  BadgeField,
  Col,
  FieldDisplay,
  humanReadableDateTime,
  IDField,
  ThemeColor,
  TinyTable,
} from '@rematter/ui-web';

import { purchaseOrderStatusLabels } from '../enumLabels';

import { protoTimestampToZonedDateTimeString } from '@/audit-log/_components/_shared';
import { getLocale } from '~/shared/utils/locale';

const getStatusColor = (status: PurchaseOrderStatus): ThemeColor => {
  switch (status) {
    case PurchaseOrderStatus.DRAFT:
      return 'neutral';
    case PurchaseOrderStatus.OPEN:
      return 'primary';
    case PurchaseOrderStatus.CLOSED:
      return 'success';
    case PurchaseOrderStatus.VOIDED:
      return 'failure';
    default:
      return 'neutral';
  }
};

type PurchaseOrderCreatedDetailProps = {
  event: PurchaseOrderCreatedEvent;
};

export const PurchaseOrderCreatedDetail = ({
  event,
}: PurchaseOrderCreatedDetailProps) => {
  const purchaseOrder = event.purchaseOrder;

  if (!purchaseOrder) {
    return (
      <Col className="@PurchaseOrderCreatedDetail items-center justify-center p-4 text-neutral/40">
        <span>No data available</span>
      </Col>
    );
  }

  const startDateStr = protoTimestampToZonedDateTimeString(
    purchaseOrder.startDate,
  );
  const endDateStr = protoTimestampToZonedDateTimeString(purchaseOrder.endDate);

  return (
    <Col className="@PurchaseOrderCreatedDetail gap-3" gap3>
      <Col className="rounded-md border border-neutral/10 bg-white p-3" gap2>
        <span className="text-xs font-medium text-neutral/60">
          Purchase Order Details
        </span>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <IDField label="ID" value={purchaseOrder.id?.value} />
          <FieldDisplay
            label="Document Number"
            value={purchaseOrder.documentNumber}
          />
          <BadgeField
            label="Status"
            value={purchaseOrderStatusLabels[purchaseOrder.status]}
            color={getStatusColor(purchaseOrder.status)}
          />
          <IDField
            label="Facility ID"
            value={purchaseOrder.facilityId?.value}
          />
          <IDField label="Account ID" value={purchaseOrder.accountId?.value} />
          <IDField
            label="Account Contact ID"
            value={purchaseOrder.accountContactId?.value}
          />
          <FieldDisplay
            label="Counterparty Number"
            value={purchaseOrder.counterpartyNumber}
          />
          <FieldDisplay
            label="Payment Currency"
            value={purchaseOrder.paymentCurrency}
          />
          {startDateStr && (
            <FieldDisplay
              label="Start Date"
              value={humanReadableDateTime(startDateStr, {
                locale: getLocale(),
              })}
            />
          )}
          {endDateStr && (
            <FieldDisplay
              label="End Date"
              value={humanReadableDateTime(endDateStr, { locale: getLocale() })}
            />
          )}
        </div>
      </Col>

      {purchaseOrder.lineItems && purchaseOrder.lineItems.length > 0 && (
        <Col className="rounded-md border border-neutral/10 bg-white p-3" gap1>
          <span className="mb-2 text-xs font-medium text-neutral/60">
            Line Items ({purchaseOrder.lineItems.length})
          </span>
          <TinyTable
            rowKey={(item) => item.id?.value ?? String(Math.random())}
            columns={[
              {
                key: 'itemId',
                label: 'Item ID',
                cell: (item) => (
                  <span className="font-mono text-xs">
                    {item.itemId?.value ?? '-'}
                  </span>
                ),
              },
              {
                key: 'quantity',
                label: 'Quantity',
                width: 100,
                cell: (item) => item.quantity ?? '-',
              },
              {
                key: 'unit',
                label: 'Unit',
                width: 120,
                cell: (item) => item.unit ?? '-',
              },
            ]}
            data={purchaseOrder.lineItems}
            isLoading={false}
          />
        </Col>
      )}
    </Col>
  );
};
